<script setup lang="ts">
/**
 * The five-step shell (port of a-studio/index.html + app.js): header (brand, rail,
 * revision chip, undo/redo, menu), banner, stage + panel (bottom sheet ≤ 1000 px), dock,
 * live region, toasts, dialogs and the print pack.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Icon from '@/components/Icon.vue';
import SemblaroLogo from '@/components/SemblaroLogo.vue';
import Stage from '@/components/Stage.vue';
import JourneyRail from '@/components/JourneyRail.vue';
import RevisionChip from '@/components/RevisionChip.vue';
import AppMenu from '@/components/AppMenu.vue';
import StepReference from '@/components/StepReference.vue';
import StepCheck from '@/components/StepCheck.vue';
import StepShape from '@/components/StepShape.vue';
import StepLook from '@/components/StepLook.vue';
import StepPack from '@/components/StepPack.vue';
import ChoiceDialog from '@/components/ChoiceDialog.vue';
import HistoryDialog from '@/components/HistoryDialog.vue';
import PhotoPicker from '@/components/PhotoPicker.vue';
import LightboxDialog from '@/components/LightboxDialog.vue';
import ExportSheet from '@/components/ExportSheet.vue';
import ToastStack from '@/components/ToastStack.vue';
import PrintPack from '@/components/PrintPack.vue';
import { app, snap, STEPS, ui, viewerRef } from '@/app/state';
import { announce } from '@/app/announce';
import { go, isCompact, setSheet } from '@/app/navigation';
import { boot, onBeforeUnload, restoreSaved, saveNow } from '@/app/persistence';
import { onAfterPrint, onBeforePrint } from '@/app/exports';
import { fmtDate } from '@/app/format';

const STEP_COMPONENTS = { reference: StepReference, check: StepCheck, shape: StepShape, look: StepLook, pack: StepPack } as const;
const stepComp = computed(() => STEP_COMPONENTS[ui.step]);
const studioEl = ref<HTMLElement | null>(null);
const gripEl = ref<HTMLButtonElement | null>(null);

const s = computed(() => snap.value);
const projName = computed(() => s.value.design.appearance?.name || 'Untitled bookcase');
const gripLabel = computed(() => (ui.sheet === 'full' ? 'Step panel expanded. Activate to show more of the model.' : ui.sheet === 'peek' ? 'Step panel lowered. Activate to raise it.' : 'Activate to expand the step panel'));

watch([projName, () => ui.step], () => {
  document.title = `${projName.value} · ${STEPS.find((x) => x.id === ui.step)!.label} · Semblaro Studio`;
}, { immediate: true });

// Undo / redo
function doUndo() { if (!app.store.canUndo) return; const l = app.store.undoLabel; app.store.undo(); announce(`Undid ${l}.`); }
function doRedo() { if (!app.store.canRedo) return; const l = app.store.redoLabel; app.store.redo(); announce(`Redid ${l}.`); }
watch(() => snap.value.design, () => {
  if (ui.sel && !snap.value.build.parts.some((p) => p.id === ui.sel)) ui.sel = null;
  if (ui.openingSel != null && ui.openingSel >= snap.value.build.openings.filter((o) => (o.bay ?? 0) === ui.bay).length) ui.openingSel = null;
  if (ui.bay >= snap.value.build.bays.length) ui.bay = 0;
});

function onKeydown(e: KeyboardEvent) {
  const mod = e.metaKey || e.ctrlKey;
  if (!mod) return;
  const t = e.target as HTMLElement | null;
  // any text entry keeps native text undo; the design undo is for everything else
  const textEntry = t?.closest?.('input:not([type="checkbox"]):not([type="radio"]):not([type="button"]), textarea, select, [contenteditable="true"]');
  const k = e.key.toLowerCase();
  if (k === 'z' && !textEntry) { e.preventDefault(); if (e.shiftKey) doRedo(); else doUndo(); }
  else if (k === 'y' && e.ctrlKey && !textEntry) { e.preventDefault(); doRedo(); }
  else if (k === 's') { e.preventDefault(); void saveNow(); }
}
function onPopState() { if (ui.booted) go(location.hash.slice(1) || 'reference', { replace: true }); }

// Toasts sit just above the docked actions on phones and tablets
function placeToasts() {
  const d = document.getElementById('dock')?.getBoundingClientRect();
  const gap = d && d.height ? Math.max(12, window.innerHeight - d.top + 10) : 24;
  document.documentElement.style.setProperty('--toast-bottom', `${Math.round(gap)}px`);
}
watch(() => [ui.step, ui.sheet, ui.toasts.length], () => requestAnimationFrame(placeToasts));

// Focus is never dropped: if the focused element left the DOM, go to the step title
let lastFocus: HTMLElement | null = null;
function onFocusIn(e: FocusEvent) { lastFocus = e.target as HTMLElement; }
function onFocusOut() {
  requestAnimationFrame(() => requestAnimationFrame(() => {
    if (document.activeElement && document.activeElement !== document.body) return;
    if (document.querySelector('dialog[open]')) return;
    if (lastFocus && !lastFocus.isConnected) document.getElementById('panelTitle')?.focus({ preventScroll: true });
  }));
}

// Bottom sheet: draggable grip with detents (peek · half · full), tap toggles, arrows step
let drag: { y0: number; h0: number; moved: boolean; last: number; lastT: number; v: number } | null = null;
const detents = () => {
  const h = studioEl.value?.clientHeight ?? 0;
  return { peek: h * 0.68, half: h * 0.44, full: Math.max(h * 0.26, Math.min(260, h * 0.4)) };
};
function gripDown(e: PointerEvent) {
  if (!isCompact()) return;
  const stage = document.getElementById('stage');
  drag = { y0: e.clientY, h0: stage?.getBoundingClientRect().height ?? 0, moved: false, last: e.clientY, lastT: performance.now(), v: 0 };
  gripEl.value?.setPointerCapture(e.pointerId);
  studioEl.value?.classList.add('is-sheet-drag');
}
function gripMove(e: PointerEvent) {
  if (!drag) return;
  const dy = e.clientY - drag.y0;
  if (Math.abs(dy) > 6) drag.moved = true;
  const now = performance.now();
  drag.v = (e.clientY - drag.last) / Math.max(1, now - drag.lastT);
  drag.last = e.clientY;
  drag.lastT = now;
  const d = detents();
  studioEl.value?.style.setProperty('--stage-h', `${Math.min(d.peek, Math.max(d.full, drag.h0 + dy))}px`);
}
function gripUp() {
  if (!drag) return;
  studioEl.value?.classList.remove('is-sheet-drag');
  const { moved, v } = drag;
  drag = null;
  if (!moved) { setSheet(ui.sheet === 'full' ? 'half' : 'full'); return; }
  const h = document.getElementById('stage')?.getBoundingClientRect().height ?? 0;
  const projected = h + v * 220;
  const order = Object.entries(detents()).sort((a, b) => Math.abs(a[1] - projected) - Math.abs(b[1] - projected));
  setSheet(order[0]![0] as 'peek' | 'half' | 'full');
}
function gripClick(e: MouseEvent) {
  // keyboard activation (pointer taps are handled on pointerup)
  if (e.detail === 0) setSheet(ui.sheet === 'full' ? 'half' : 'full');
}
function gripKey(e: KeyboardEvent) {
  const seq = ['full', 'half', 'peek'] as const;
  const i = seq.indexOf(ui.sheet);
  if (e.key === 'ArrowUp' && i > 0) { e.preventDefault(); setSheet(seq[i - 1]!); }
  if (e.key === 'ArrowDown' && i < 2) { e.preventDefault(); setSheet(seq[i + 1]!); }
}

// Keyboard open on phones: keep the focused field above the dock
const textField = (el: Element | null) => !!el?.matches?.('input:not([type="checkbox"]):not([type="radio"]), textarea, select');
function keep() {
  const a = document.activeElement as HTMLElement | null;
  const body = document.getElementById('panelBody');
  if (!a || !body?.contains(a) || !textField(a)) return;
  requestAnimationFrame(() => a.scrollIntoView({ block: 'center', behavior: 'smooth' }));
}
function panelFocusIn(e: FocusEvent) {
  if (!isCompact() || !textField(e.target as Element) || !(e.target as Element).closest('#panelBody')) return;
  document.body.classList.add('field-focus');
  setTimeout(() => { viewerRef.value?.resize(); keep(); }, 280);
}
function panelFocusOut() {
  setTimeout(() => {
    if (textField(document.activeElement) && document.getElementById('panelBody')?.contains(document.activeElement)) return;
    if (document.body.classList.contains('field-focus')) { document.body.classList.remove('field-focus'); setTimeout(() => viewerRef.value?.resize(), 300); }
  }, 60);
}
function onViewport() {
  const vv = window.visualViewport;
  if (!vv) return;
  document.documentElement.style.setProperty('--vvh', `${vv.height}px`);
  document.body.classList.toggle('kb-open', isCompact() && vv.height < window.innerHeight * 0.8);
  keep();
}
function onResize() { requestAnimationFrame(placeToasts); }

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('focusin', onFocusIn);
  document.addEventListener('focusout', onFocusOut);
  window.addEventListener('popstate', onPopState);
  window.addEventListener('beforeunload', onBeforeUnload);
  window.addEventListener('beforeprint', onBeforePrint);
  window.addEventListener('afterprint', onAfterPrint);
  window.addEventListener('resize', onResize);
  window.visualViewport?.addEventListener('resize', onViewport);
  if (isCompact()) setSheet('half');
  void boot().then(() => { if (isCompact()) setSheet(ui.step === 'pack' ? 'full' : 'half'); placeToasts(); });
});
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown);
  document.removeEventListener('focusin', onFocusIn);
  document.removeEventListener('focusout', onFocusOut);
  window.removeEventListener('popstate', onPopState);
  window.removeEventListener('beforeunload', onBeforeUnload);
  window.removeEventListener('beforeprint', onBeforePrint);
  window.removeEventListener('afterprint', onAfterPrint);
  window.removeEventListener('resize', onResize);
  window.visualViewport?.removeEventListener('resize', onViewport);
});
function retryBoot() { void boot(); }
</script>

<template>
  <a class="skip" href="#panelBody">Skip to the step panel</a>

  <header id="top" class="top">
    <div class="brand">
      <span class="brand-logo"><SemblaroLogo /><SemblaroLogo variant="mark" /></span>
      <div class="brand-text">
        <span id="projName" class="proj">{{ projName }}</span>
      </div>
    </div>
    <JourneyRail />
    <div class="top-actions">
      <RevisionChip />
      <div class="undo-pair" role="group" aria-label="Undo and redo">
        <button id="undoBtn" type="button" class="icon-btn" :disabled="!s.canUndo" :aria-label="s.canUndo ? `Undo ${s.undoLabel}` : 'Undo'" :title="s.canUndo ? `Undo ${s.undoLabel} (⌘Z)` : 'Nothing to undo'" @click="doUndo"><Icon name="undo" /></button>
        <button id="redoBtn" type="button" class="icon-btn" :disabled="!s.canRedo" :aria-label="s.canRedo ? `Redo ${s.redoLabel}` : 'Redo'" :title="s.canRedo ? `Redo ${s.redoLabel} (⇧⌘Z)` : 'Nothing to redo'" @click="doRedo"><Icon name="redo" /></button>
      </div>
      <AppMenu />
    </div>
  </header>

  <div v-if="ui.restoreOffer" id="banner" class="banner banner--info" role="region" aria-label="Project status">
    <Icon name="save" />
    <p>A newer copy of this project is saved on this device from <strong>{{ fmtDate(ui.restoreOffer.savedAt) }}</strong>. <span class="banner-sub">The project file is older.</span></p>
    <div class="banner-acts">
      <button type="button" class="btn btn--quiet btn--sm" data-act="dismiss-restore" @click="ui.restoreOffer = null">Not now</button>
      <button type="button" class="btn btn--ink btn--sm" data-act="restore" @click="restoreSaved">Restore it</button>
    </div>
  </div>
  <div v-else id="banner" class="banner" role="region" aria-label="Project status" hidden />

  <main id="studio" ref="studioEl" class="studio" :class="{ 'step-in': ui.stepIn }" :data-step="ui.step" :data-sheet="ui.sheet">
    <div v-if="ui.bootError" class="boot-error card" role="alert">
      <h1 class="h-display">The studio could not start</h1>
      <p>{{ ui.bootError }}</p>
      <button type="button" class="btn btn--ink" @click="retryBoot">Try again</button>
    </div>
    <template v-else>
      <Stage />
      <aside id="panel" class="panel" :data-width="ui.step === 'pack' ? 'wide' : 'normal'" aria-labelledby="panelTitle" @focusin="panelFocusIn" @focusout="panelFocusOut">
        <button
          id="sheetGrip"
          ref="gripEl"
          type="button"
          class="sheet-grip"
          :aria-label="gripLabel"
          :aria-expanded="ui.sheet === 'full' ? 'true' : 'false'"
          @pointerdown="gripDown"
          @pointermove="gripMove"
          @pointerup="gripUp"
          @pointercancel="gripUp"
          @click="gripClick"
          @keydown="gripKey"
        ><span /></button>
        <component :is="stepComp" v-if="ui.booted" :key="ui.step" />
        <div v-else class="panel-body" role="status"><p class="muted">Opening your project…</p></div>
      </aside>
    </template>
  </main>

  <div id="live" class="sr-only" aria-live="polite" aria-atomic="true" />
  <ToastStack />
  <ChoiceDialog />
  <HistoryDialog />
  <PhotoPicker />
  <LightboxDialog />
  <ExportSheet />
  <PrintPack v-if="ui.booted" />
</template>
